// src/pages/Signup.js
import React, { useState, useEffect } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Link, useNavigate, useLocation } from "react-router-dom";

import {
  auth,
  db,
  googleProvider,
  signWith,
  resolveRedirectResult,
} from "../firebaseConfig";

import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { ensureUserProfile, PLAN_LIMITS } from "../services/userService";
import { ALWAYS_PREMIUM } from "../config/featureFlags";
import { createPremiumCheckoutSession } from "../services/subscriptionService";

const Signup = () => {
  // Visibilité du mdp
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Form
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [username, setUsername] = useState("");

  // UI
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const requestedPlan = searchParams.get("plan");
  const wantsPremium = requestedPlan === "premium";
  const [isLaunchingCheckout, setIsLaunchingCheckout] = useState(false);
  const [redirectChecked, setRedirectChecked] = useState(false);

  // Résultat d'un signInWithRedirect (mobile)
  useEffect(() => {
    // Éviter les appels multiples (React StrictMode en dev appelle 2 fois)
    if (redirectChecked) {
      if (process.env.NODE_ENV !== 'production') console.log("Redirect already checked, skipping");
      return;
    }
    
    setRedirectChecked(true);
    
    // Test: Essayer de créer un cookie pour vérifier si c'est autorisé
    document.cookie = "test_cookie=1; SameSite=None; Secure";
    const cookiesEnabled = document.cookie.indexOf("test_cookie") !== -1;
    if (process.env.NODE_ENV !== 'production') console.log("🍪 Cookies enabled:", cookiesEnabled);
    
    // Test: localStorage disponible ?
    try {
      localStorage.setItem("test_storage", "1");
      const storageEnabled = localStorage.getItem("test_storage") === "1";
      localStorage.removeItem("test_storage");
      if (process.env.NODE_ENV !== 'production') console.log("💾 localStorage enabled:", storageEnabled);
      
      // Vérifier si Firebase a stocké quelque chose
      const firebaseKeys = Object.keys(localStorage).filter(k => k.includes("firebase"));
      if (process.env.NODE_ENV !== 'production') console.log("🔑 Firebase keys in localStorage:", firebaseKeys.length);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.error("❌ localStorage blocked:", e);
    }
    
    if (process.env.NODE_ENV !== 'production') console.log("Checking for redirect result...");
    
    resolveRedirectResult()
      .then(async (res) => {
        if (process.env.NODE_ENV !== 'production') console.log("Redirect result:", res);
        if (res && res.user) {
          if (process.env.NODE_ENV !== 'production') console.log("User found from redirect:", res.user.email);
          setSuccess("Création du profil...");
          await ensureUserProfile(res.user);
          // Si besoin, on peut créer/compléter le doc utilisateur ici
          if (wantsPremium) {
            await startPremiumCheckout(res.user, "oauth_redirect");
          } else {
            setSuccess("Inscription réussie ! Redirection…");
            setTimeout(() => navigate("/dashboard"), 600);
          }
        } else {
          if (process.env.NODE_ENV !== 'production') console.log("No redirect result found");
        }
      })
      .catch((e) => {
        if (process.env.NODE_ENV !== 'production') console.error("Redirect error:", e);
        setError(mapAuthError(e));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Social handlers (créent aussi le compte s'il n'existe pas) ---
  const signGoogle = async () => {
    if (isLaunchingCheckout) return;
    setError(""); setSuccess("");
    try {
      const res = await signWith(googleProvider);
      if (res?.user) {
        await ensureUserProfile(res.user);
        if (wantsPremium) {
          await startPremiumCheckout(res.user, "oauth_google");
        } else {
          setSuccess("Connecté avec Google !");
          setTimeout(() => navigate("/dashboard"), 600);
        }
      }
    } catch (e) { setError(mapAuthError(e)); }
  };


  // --- Email / Password ---
  const handleSignup = async (e) => {
    e.preventDefault();
    if (isLaunchingCheckout) return;
    setError("");
    setSuccess("");

    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await setDoc(doc(db, "users", userCredential.user.uid), {
        username,
        email,
        plan: ALWAYS_PREMIUM ? "premium" : "free",
        subscriptionStatus: ALWAYS_PREMIUM ? "premium" : "free",
        limits: ALWAYS_PREMIUM ? { ...PLAN_LIMITS.premium } : { ...PLAN_LIMITS.free },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Bypass checkout if ALWAYS_PREMIUM
      if (!ALWAYS_PREMIUM && wantsPremium) {
        await startPremiumCheckout(userCredential.user, "email_signup");
      } else {
        setUsername("");
        setEmail("");
        setPassword("");
        setConfirmPassword("");
        setSuccess("Inscription réussie ! Redirection…");
        setTimeout(() => navigate("/dashboard"), 800);
      }
    } catch (err) {
      console.error("Erreur lors de l'inscription :", err);
      if (err.code === "auth/email-already-in-use") {
        setError("Cet email est déjà utilisé");
      } else if (err.code === "auth/weak-password") {
        setError("Le mot de passe doit faire au moins 6 caractères");
      } else {
        setError(mapAuthError(err));
      }
    }
  };

  const startPremiumCheckout = async (user, source = "signup_premium") => {
    try {
      setError("");
      setSuccess("Redirection vers Stripe…");
      setIsLaunchingCheckout(true);
      const url = await createPremiumCheckoutSession({
        user,
        metadata: { source },
      });
      window.location.href = url;
    } catch (checkoutError) {
      console.error("Erreur lors du lancement du checkout Stripe :", checkoutError);
      setIsLaunchingCheckout(false);
      setError(
        checkoutError?.message ||
          "Impossible de lancer le paiement. Vérifiez votre connexion et réessayez."
      );
      setSuccess("");
    }
  };

  return (
    <div className="min-h-screen bg-[#EAEEFE] px-4 flex flex-col md:flex-row md:items-center md:justify-center relative pb-6 md:pb-0 pt-24 md:pt-0">
      {/* Logo en haut à gauche */}
      <Link to="/">
        <header className="absolute top-4 left-4 flex items-center space-x-2 z-10">
          <img src="/Logo1.png" alt="DressCode Logo" className="h-10 md:h-12" />
          <img src="/Logo2.png" alt="DressCode Subtitle Logo" className="h-3 md:h-4" />
        </header>
      </Link>

      {/* Conteneur principal */}
      <div className="grid grid-cols-1 md:grid-cols-2 items-start md:items-center justify-center max-w-[70rem] w-full gap-8 md:gap-24 px-4 relative mt-12 md:mt-0">
        {/* Section gauche */}
        <div className="flex flex-col justify-center max-w-md w-full mx-auto text-center md:text-left">
          <h1 className="text-[32px] sm:text-[40px] md:text-[50px] font-semibold text-gray-800 leading-tight mb-3 md:mb-4 text-center md:text-left">
            Rejoignez-nous
          </h1>
          <p className="text-[20px] sm:text-[24px] md:text-[35px] font-medium text-gray-600 mb-4 md:mb-6 text-center md:text-left">
            Organisez votre garde-robe en un clic.
          </p>
          <p className="text-[16px] text-gray-600 text-center md:text-left relative z-10">
            Vous avez déjà un compte ?
            <br />
            <Link to="/login" className="text-[16px] text-[#4D47C3] font-semibold hover:underline">
              Connectez-vous ici !
            </Link>
          </p>
        </div>

        {/* Section droite */}
        <div className="w-full md:w-full md:p-16 mx-auto md:mx-0">
          <h2 className="text-[24px] md:text-[30px] font-medium text-gray-800 mb-4 md:mb-6 text-left">
            Créez votre compte
          </h2>

          {error && <p className="text-red-500 mb-4">{error}</p>}
          {success && <p className="text-green-500 mb-4">{success}</p>}

          <form className="space-y-4 md:space-y-6" onSubmit={handleSignup}>
            {/* Nom d'utilisateur */}
            <div className="relative shadow-lg" style={{ boxShadow: "0px 24px 10px rgba(36, 38, 43, 0.06)" }}>
              <input
                type="text"
                id="username"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full md:w-[369px] h-[52px] md:h-[56px] px-4 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none placeholder-violet text-base md:text-lg"
              />
            </div>

            {/* Adresse e-mail */}
            <div className="relative shadow-lg" style={{ boxShadow: "0px 24px 10px rgba(36, 38, 43, 0.06)" }}>
              <input
                type="email"
                id="email"
                placeholder="Enter Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full md:w-[369px] h-[52px] md:h-[56px] px-4 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none placeholder-violet text-base md:text-lg"
              />
            </div>

            {/* Mot de passe */}
            <div className="relative shadow-lg" style={{ boxShadow: "0px 24px 10px rgba(36, 38, 43, 0.06)" }}>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full md:w-[369px] h-[52px] md:h-[56px] px-4 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none placeholder-violet text-base md:text-lg"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#A7A3FF]"
              >
                {showPassword ? <FaEyeSlash className="h-5 w-5" /> : <FaEye className="h-5 w-5" />}
              </button>
            </div>

            {/* Confirmation du mot de passe */}
            <div className="relative shadow-lg" style={{ boxShadow: "0px 24px 10px rgba(36, 38, 43, 0.06)" }}>
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirm-password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full md:w-[369px] h-[52px] md:h-[56px] px-4 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none placeholder-violet text-base md:text-lg"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#A7A3FF]"
              >
                {showConfirmPassword ? <FaEyeSlash className="h-5 w-5" /> : <FaEye className="h-5 w-5" />}
              </button>
            </div>

            {/* Bouton d'inscription */}
            <button
              type="submit"
              disabled={isLaunchingCheckout}
              className="w-full md:w-[369px] h-[52px] md:h-[56px] bg-black text-white rounded-lg shadow-lg hover:bg-gray-800 disabled:opacity-70 disabled:cursor-not-allowed text-base md:text-lg"
            >
              {isLaunchingCheckout ? "Redirection..." : "Créer un compte"}
            </button>
          </form>

          {/* Google Sign In */}
          <div className="flex items-center my-8">
            <div className="flex-grow h-px bg-gray-300"></div>
            <span className="px-4 text-gray-500 text-sm">ou continuer avec</span>
            <div className="flex-grow h-px bg-gray-300"></div>
          </div>

          <div className="flex justify-center mb-6 md:mb-0">
            <button
              onClick={signGoogle}
              disabled={isLaunchingCheckout}
              className="w-full md:w-[369px] h-[52px] md:h-[56px] flex items-center justify-center gap-3 bg-white border-2 border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 hover:border-gray-400 transition-all disabled:opacity-60 disabled:cursor-not-allowed text-base md:text-lg"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span className="text-gray-700 font-medium">Continuer avec Google</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function mapAuthError(err) {
  if (!err) return "Erreur de connexion";
  
  // Log l'erreur pour le debug (visible dans la console uniquement)
  console.error("Auth error:", err.code, err.message);
  
  switch (err.code) {
    case "auth/user-not-found":
      return "Aucun compte associé à cet identifiant";
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Identifiants incorrects";
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "Connexion annulée";
    case "auth/popup-blocked":
      return "Pop-up bloquée. Autorisez les pop-ups pour ce site";
    case "auth/account-exists-with-different-credential":
      return "Un compte existe déjà avec un autre mode de connexion";
    case "auth/network-request-failed":
      return "Problème de connexion. Vérifiez votre internet";
    case "auth/too-many-requests":
      return "Trop de tentatives. Réessayez plus tard";
    case "auth/invalid-email":
      return "Adresse email invalide";
    case "auth/email-already-in-use":
      return "Cet email est déjà utilisé";
    case "auth/weak-password":
      return "Le mot de passe doit faire au moins 6 caractères";
    case "auth/operation-not-allowed":
      return "Méthode de connexion non disponible";
    case "auth/user-disabled":
      return "Ce compte a été désactivé";
    default:
      // Ne JAMAIS afficher err.message (trop technique)
      return "Une erreur s'est produite. Veuillez réessayer";
  }
}

export default Signup;
